import { omit } from 'lodash';
import { InternalRule, SearchContext } from '../types';
import { Action, ActionType, ReducerState } from './reducerTypes';
import {
  addNewRowToState,
  calculateMoveRow,
  fillEmptyQueries,
  getEmptyQuery,
  getEmptyRule,
  getUuid,
  isHasReferenceFilterId,
  removeRowFromState,
} from './utils';
import { APIEntityType, Operator } from '@ardoq/api-types';

export const ROOT_UUID = getUuid();

export const DEFAULT_STATE: ReducerState = {
  searchContext: SearchContext.COMPONENT,
  map: { [ROOT_UUID]: getEmptyQuery(ROOT_UUID) },
  tree: { [ROOT_UUID]: [] },
  rootUuid: ROOT_UUID,
  maxSubqueryNestingLevel: 2,
};

export const queryBuilderReducer = (
  state: ReducerState = DEFAULT_STATE,
  action: Action
): ReducerState => {
  if (action.type === ActionType.MOVE_ROW) {
    return calculateMoveRow(state, action);
  }
  if (action.type === ActionType.ADD_ROW) {
    const isAddingQuery = action.rowType === 'query';
    const newRow = isAddingQuery
      ? getEmptyQuery(getUuid())
      : getEmptyRule({ uuid: getUuid() });
    return fillEmptyQueries(addNewRowToState(state, action.parentUuid, newRow));
  }
  if (action.type === ActionType.REMOVE_ROW) {
    const { uuid } = action;
    if (uuid === state.rootUuid) return state;
    return fillEmptyQueries(removeRowFromState(state, uuid));
  }
  if (action.type === ActionType.UPDATE_ROW) {
    const updatePartial = omit(action, 'type');
    const currentRow = state.map[action.uuid];
    if (updatePartial.field) {
      return {
        ...state,
        map: {
          ...state.map,
          [action.uuid]: {
            ...getEmptyRule(updatePartial),
            ...(isHasReferenceFilterId(updatePartial.field)
              ? { targetEntityType: APIEntityType.COMPONENT }
              : {}),
          },
        },
      };
    }
    // setting operator to IS_EMPTY or IS_NOT_EMPTY should flush the value of current rule
    if (
      updatePartial.operator &&
      [Operator.IS_NOT_EMPTY, Operator.IS_EMPTY].includes(
        updatePartial.operator
      )
    ) {
      return {
        ...state,
        map: {
          ...state.map,
          [action.uuid]: { ...currentRow, ...updatePartial, value: null },
        },
      };
    }
    if (
      isHasReferenceFilterId((currentRow as InternalRule).field) &&
      updatePartial.targetEntityType &&
      (currentRow as InternalRule).value
    ) {
      // clear value if targetEntityType is changed
      return {
        ...state,
        map: {
          ...state.map,
          [action.uuid]: { ...currentRow, value: null, ...updatePartial },
        },
      };
    }

    return {
      ...state,
      map: {
        ...state.map,
        [action.uuid]: { ...currentRow, ...updatePartial },
      },
    };
  }
  if (action.type === ActionType.UPDATE_SEARCH_CONTEXT) {
    const { searchContext } = action;
    return fillEmptyQueries({ ...DEFAULT_STATE, searchContext });
  }
  return state;
};
